import { createContext, useContext } from 'react';
import { useLayoutEditorForm } from '../../use/editor/useLayoutEditorForm';
import { useLayoutEditorEntity } from '../../use/editor/useLayoutEditorEntity';
import { useLayoutEditorCompositeField } from '../../components/CompositeField/useLayoutEditorCompositeField';
import type { ArTableType, TypeId } from '#types/System.Artifacts.types';
import type { VisibilityTypeId } from '#types/System.types';

export type ColumnModification = {
    visibilityTypes?: VisibilityTypeId[];
    kitMemberHiddenTypes?: VisibilityTypeId[];
    groupMemberHiddenTypes?: VisibilityTypeId[];
};

/**
 * Table types data included in the layout editor context.
 * Includes modification tracking for column visibility changes.
 */
type TableTypesData = {
    tableTypes: ArTableType[];
    tableTypesLoading: boolean;
    /** Map of tableId -> columnTypeId -> modification */
    tableTypeModifications: Map<string, Map<TypeId, ColumnModification>>;
    /** Whether there are unsaved table type modifications */
    hasTableTypeModifications: boolean;
    updateTableColumnVisibility: (tableId: string, columnTypeId: TypeId, visibilityTypes: VisibilityTypeId[]) => void;
    updateTableColumnKitMemberHidden: (tableId: string, columnTypeId: TypeId, kitMemberHiddenTypes: VisibilityTypeId[]) => void;
    updateTableColumnGroupMemberHidden: (tableId: string, columnTypeId: TypeId, groupMemberHiddenTypes: VisibilityTypeId[]) => void;
    /** Save all pending table type modifications */
    saveTableTypeModifications: () => Promise<void>;
};

/**
 * Type-safe discriminated union of form and entity editor contexts.
 * Includes table types data for ArTable element configuration.
 */
export type LayoutEditorContextType =
    | (ReturnType<typeof useLayoutEditorForm> & { type: 'form' } & TableTypesData)
    | (ReturnType<typeof useLayoutEditorEntity> & { type: 'entity' } & TableTypesData)
    | (ReturnType<typeof useLayoutEditorCompositeField> & { type: 'compositeField' } & TableTypesData);

/**
 * Context for the Layout Editor functionality.
 * Provides a unified interface for both form and entity editing.
 */
export const LayoutEditorContext = createContext<LayoutEditorContextType | null>(null);

/**
 * Hook to access the Layout Editor context.
 * Provides type-safe access to either form or entity editing functionality.
 * 
 * @throws {Error} If used outside of a LayoutEditorProvider
 * @returns Type-safe context value based on the provider type
 */
export const useLayoutEditorContext = () => {
    const context = useContext(LayoutEditorContext);
    if (!context) {
        throw new Error('useLayoutEditorContext must be used within a LayoutEditorProvider');
    }
    return context;
};